import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getMarketplaceBookingLedgerTimeline,
  getMarketplaceBookingReceipts,
  listMyMarketplaceBookings,
} from "../../api/myboothshareBilling";
import { useAuth } from "../../context/AuthContext";
import MarketplaceShell from "./MarketplaceShell";
import {
  formatMarketplaceDateTime,
  formatMarketplaceMoneyFromCents,
  getMarketplaceBookingLabel,
  getMarketplaceReleaseLabel,
  getWorkspaceFinanceStateLabel,
  getWorkspaceFinanceStateTone,
} from "../../utils/myboothshareBillingPresentation";
import { buildWorkspaceOperationalBoundary } from "../../utils/workspaceOperationalBoundaries";
import { titleCaseLabel } from "../../utils/workspacePresentation";

function HeroPill({ children, tone = "default" }) {
  const toneClasses =
    tone === "solid"
      ? "border-white/15 bg-white/[0.12] text-white"
      : "border-white/15 bg-white/[0.05] text-slate-100";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-semibold ${toneClasses}`}
    >
      {children}
    </span>
  );
}

function SnapshotItem({ label, value, detail = "" }) {
  return (
    <div>
      <div className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-300">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-white">{value}</div>
      {detail ? <div className="mt-1 text-sm text-slate-300">{detail}</div> : null}
    </div>
  );
}

function BookingDetail({ label, value, detail = "" }) {
  return (
    <div className="rounded-[1.2rem] border border-slate-200 bg-white px-4 py-4">
      <div className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-base font-semibold text-slate-950">{value}</div>
      {detail ? <div className="mt-1 text-sm text-slate-500">{detail}</div> : null}
    </div>
  );
}

export default function MarketplaceAccountBookingsPage() {
  const { token, user } = useAuth();
  const [items, setItems] = useState([]);
  const [financeByBooking, setFinanceByBooking] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError("");
    listMyMarketplaceBookings(token)
      .then((response) => {
        if (active) {
          setItems(Array.isArray(response?.items) ? response.items : []);
        }
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail ||
              err.message ||
              "Unable to load your Ellyanna Boothshare rentals."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !items.length) return undefined;
    let active = true;
    Promise.all(
      items.map(async (item) => {
        try {
          const [timeline, receipts] = await Promise.all([
            getMarketplaceBookingLedgerTimeline(token, item.booking_id),
            getMarketplaceBookingReceipts(token, item.booking_id),
          ]);
          return [item.booking_id, { timeline, receipts }];
        } catch (err) {
          return [item.booking_id, { timeline: null, receipts: null }];
        }
      })
    ).then((rows) => {
      if (!active) return;
      setFinanceByBooking(
        rows.reduce((acc, [bookingId, value]) => {
          acc[bookingId] = value;
          return acc;
        }, {})
      );
    });
    return () => {
      active = false;
    };
  }, [items, token]);

  const metrics = useMemo(() => {
    const active = items.filter((item) =>
      ["confirmed", "active", "completed", "payout_released"].includes(
        String(item.booking_status || "").toLowerCase()
      )
    ).length;
    const pendingRelease = items.filter(
      (item) => String(item.transfer_status || "").toLowerCase() !== "released" && !item.dispute_open
    ).length;
    const spend = items.reduce(
      (sum, item) => sum + Number(item.amount_cents || 0),
      0
    );
    const disputes = items.filter((item) => item.dispute_open).length;
    return { active, pendingRelease, spend, disputes };
  }, [items]);

  return (
    <MarketplaceShell>
      <section className="space-y-6">
        <article className="overflow-hidden rounded-[2.15rem] border border-slate-200 bg-white shadow-sm">
          <div className="bg-[#32386f] px-7 py-7 text-white lg:px-9 lg:py-8">
            <div className="grid gap-8 lg:grid-cols-[1.2fr,0.9fr] lg:items-center">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-rose-200">
                  Ellyanna Boothshare Account
                </p>
                <h1 className="mt-4 max-w-3xl text-4xl font-semibold tracking-tight lg:text-[3.15rem]">
                  My Rentals And Payments
                </h1>
                <p className="mt-4 max-w-3xl text-base leading-8 text-slate-200 lg:max-w-2xl">
                  Review rental requests, rental agreements, payments, refunds, and dispute state.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <HeroPill tone="solid">Marketplace billing</HeroPill>
                  <HeroPill>{token ? "Signed in" : "Guest"}</HeroPill>
                  <HeroPill>{items.length ? `${items.length} rentals` : "No rentals yet"}</HeroPill>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/search"
                    className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#2f3566] hover:bg-slate-100"
                  >
                    Search Workspaces
                  </Link>
                  <Link
                    to="/account/profile"
                    className="rounded-full border border-white/15 bg-white/[0.06] px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/[0.12]"
                  >
                    Open Profile
                  </Link>
                </div>
              </div>
              <div className="rounded-[2rem] border border-white/10 bg-white/[0.08] px-6 py-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-rose-200">
                  Rental Snapshot
                </div>
                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <SnapshotItem
                    label="Confirmed"
                    value={metrics.active}
                    detail="Rental agreements with payment captured and retained in history."
                  />
                  <SnapshotItem
                    label="Pending release"
                    value={metrics.pendingRelease}
                    detail="Payments still inside the release hold window."
                  />
                  <SnapshotItem
                    label="Total paid"
                    value={formatMarketplaceMoneyFromCents(metrics.spend, "cad")}
                    detail="Total upfront payments made across this account."
                  />
                  <SnapshotItem
                    label="Disputes"
                    value={metrics.disputes}
                    detail={user?.full_name || "Marketplace renter account"}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-5 px-7 py-7 lg:px-9">
            {!token ? (
              <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 px-6 py-12 text-center text-slate-600">
                Sign in to see your Ellyanna Boothshare rental history and dispute tools.
              </div>
            ) : null}
            {loading ? <p className="text-slate-600">Loading rentals...</p> : null}
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {!loading && !error && token && items.length === 0 ? (
              <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-600">
                <div className="mx-auto max-w-xl">
                  <div className="text-lg font-semibold text-slate-900">
                    No Ellyanna Boothshare rentals are attached to this account yet.
                  </div>
                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    Once you pay for a workspace, the rental timeline, refunds, and any
                    dispute activity will show up here.
                  </p>
                </div>
                <div className="mt-5">
                  <Link
                    to="/search"
                    className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600"
                  >
                    Search Workspaces
                  </Link>
                </div>
              </div>
            ) : null}
            {!loading && !error && token && items.length ? (
              <div className="grid gap-5 md:grid-cols-3">
                <BookingDetail
                  label="Account history"
                  value={`${items.length} rentals`}
                  detail="This page shows your renter-side Ellyanna Boothshare records only."
                />
                <BookingDetail
                  label="Release hold policy"
                  value="After rental end"
                  detail="Ellyanna Boothshare holds funds until the rental ends and the review window passes."
                />
                <BookingDetail
                  label="Dispute protection"
                  value={metrics.disputes}
                  detail="Open disputes pause release until the case is resolved."
                />
              </div>
            ) : null}
            <div className="grid gap-5">
              {items.map((item) => (
                (() => {
                  const financeRecord = financeByBooking[item.booking_id] || {};
                  const timeline = financeRecord.timeline || null;
                  const receipts = financeRecord.receipts || { receipts: [], installments: [] };
                  const operationalBoundary =
                    item.operational_boundary ||
                    buildWorkspaceOperationalBoundary({
                      occupancyMode: item.occupancy_mode,
                      clientOwnershipMode: item.client_ownership_mode,
                      complianceProfileSummary:
                        item.acceptance_snapshot?.compliance_profile_summary || {},
                      ownershipRulesSummary:
                        item.acceptance_snapshot?.ownership_rules_summary || {},
                      warningStateLevel: item.acceptance_snapshot?.warning_state_level,
                      warningTypes: (item.acceptance_snapshot?.warnings || [])
                        .map((warning) => warning?.warning_type)
                        .filter(Boolean),
                    });
                  const financeTone = getWorkspaceFinanceStateTone(
                    item.finance_state_code || timeline?.finance_state
                  );
                  const financeToneClasses =
                    financeTone === "success"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : financeTone === "warning"
                      ? "border-amber-200 bg-amber-50 text-amber-700"
                      : financeTone === "danger"
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : financeTone === "accent"
                      ? "border-sky-200 bg-sky-50 text-sky-700"
                      : "border-slate-200 bg-slate-100 text-slate-700";
                  return (
                <article
                  key={item.booking_id}
                  className="rounded-[1.85rem] border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                        Rental #{item.booking_id}
                      </p>
                      <h2 className="mt-2 text-2xl font-semibold text-slate-950">
                        {item.workspace_name || `Workspace #${item.workspace_id || "n/a"}`}
                      </h2>
                      <p className="mt-2 text-sm leading-7 text-slate-600">
                        {formatMarketplaceDateTime(item.start_at_utc)} to{" "}
                        {formatMarketplaceDateTime(item.end_at_utc)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {getMarketplaceBookingLabel(item.booking_status)}
                      </span>
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                        {getMarketplaceReleaseLabel(item)}
                      </span>
                      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${financeToneClasses}`}>
                        {getWorkspaceFinanceStateLabel(
                          item.finance_state_code || timeline?.finance_state
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <BookingDetail
                      label="Paid by renter"
                      value={formatMarketplaceMoneyFromCents(item.amount_cents, item.currency)}
                      detail="Collected upfront through Stripe Checkout."
                    />
                    <BookingDetail
                      label="Pending release"
                      value={formatMarketplaceMoneyFromCents(item.net_payout_cents, item.currency)}
                      detail="The owner payout stays held until release conditions are met."
                    />
                    <BookingDetail
                      label="Refunded"
                      value={formatMarketplaceMoneyFromCents(item.refund_total_cents, item.currency)}
                      detail="Full and partial refunds stay visible in your marketplace history."
                    />
                    <BookingDetail
                      label="Estimated release"
                      value={
                        item.released_at_utc
                          ? formatMarketplaceDateTime(item.released_at_utc)
                          : item.scheduled_release_at_utc
                          ? formatMarketplaceDateTime(item.scheduled_release_at_utc)
                          : "Pending"
                      }
                      detail="Release timing may pause if a dispute is opened."
                    />
                  </div>

                  <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <BookingDetail
                      label="Accepted mode"
                      value={titleCaseLabel(item.occupancy_mode)}
                      detail="Stored when you accepted this rental flow."
                    />
                    <BookingDetail
                      label="Client ownership"
                      value={titleCaseLabel(item.client_ownership_mode)}
                      detail="This affects rental terms, CRM access, and payout expectations."
                    />
                    <BookingDetail
                      label="Agreement"
                      value={
                        item.agreement?.accepted_version || "Not recorded"
                      }
                      detail={
                        item.agreement?.accepted_at
                          ? `Accepted ${formatMarketplaceDateTime(
                              item.agreement.accepted_at
                            )}`
                          : "No accepted agreement snapshot is attached yet."
                      }
                    />
                    <BookingDetail
                      label="Finance state"
                      value={getWorkspaceFinanceStateLabel(
                        item.finance_state_code || timeline?.finance_state
                      )}
                      detail={
                        timeline
                          ? `${formatMarketplaceMoneyFromCents(
                              item.due_now_cents || Math.round(Number(item.finance_state?.amount_due_now || 0) * 100),
                              item.currency
                            )} due now, ${formatMarketplaceMoneyFromCents(
                              item.due_later_cents || 0,
                              item.currency
                            )} due later.`
                          : item.finance_state?.payment_timing ||
                        "Payment timing is not available."
                      }
                    />
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                    <div className="font-semibold text-slate-900">
                      Operational boundary
                    </div>
                    <p className="mt-2">{operationalBoundary.relationshipDescription}</p>
                    <p className="mt-2">
                      <span className="font-semibold text-slate-900">Scheduling:</span>{" "}
                      {operationalBoundary.schedulingScopeLabel}
                    </p>
                    <p className="mt-2">
                      <span className="font-semibold text-slate-900">Reporting:</span>{" "}
                      {operationalBoundary.reportingScopeLabel}
                    </p>
                    <p className="mt-2">
                      <span className="font-semibold text-slate-900">Agreement:</span>{" "}
                      {operationalBoundary.agreementScopeLabel}
                    </p>
                  </div>

                  {timeline ? (
                    <div className="mt-4 grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                        <div className="font-semibold text-slate-900">Finance timeline</div>
                        <div className="mt-3 space-y-3">
                          {(timeline.entries || []).slice(-4).reverse().map((entry) => (
                            <div key={entry.id} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-semibold text-slate-900">
                                  {titleCaseLabel(entry.entry_type)}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {formatMarketplaceDateTime(entry.occurred_at)}
                                </span>
                              </div>
                              <div className="mt-1 text-slate-600">{entry.description}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                        <div className="font-semibold text-slate-900">Receipts and statements</div>
                        <div className="mt-3 space-y-3">
                          {(receipts.receipts || []).slice(0, 3).map((receipt) => (
                            <div key={receipt.id} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                              <div className="font-semibold text-slate-900">{receipt.title}</div>
                              <div className="mt-1">
                                {formatMarketplaceMoneyFromCents(receipt.amount_cents, receipt.currency)} ·{" "}
                                {formatMarketplaceDateTime(receipt.issued_at)}
                              </div>
                            </div>
                          ))}
                          {!receipts.receipts?.length ? (
                            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-3 py-3">
                              Receipts will appear here as payments, refunds, and statements are issued.
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {Array.isArray(item.acceptance_snapshot?.disclosures) &&
                  item.acceptance_snapshot.disclosures.length ? (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                      <div className="font-semibold text-slate-900">
                        Accepted disclosures
                      </div>
                      <div className="mt-2 space-y-2">
                        {item.acceptance_snapshot.disclosures.map((disclosure) => (
                          <p key={disclosure}>{disclosure}</p>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {item.dispute_open ? (
                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                      A dispute is open for this rental, so payout release is paused while the
                      case is reviewed.
                    </div>
                  ) : null}

                  {item.owner_upgrade_required ? (
                    <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
                      Future independent owner setup is still required for this
                      account.
                    </div>
                  ) : null}

                  {operationalBoundary.independentRenter ? (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Independent renter mode does not make this rental part of normal salon staff workflow by default.
                    </div>
                  ) : null}

                  {operationalBoundary.warningStateLevel === "high_risk" ? (
                    <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      This rental carries high-risk control contradictions for an independent renter. Review the stored ownership, agreement, and finance terms carefully.
                    </div>
                  ) : null}

                  {item.salon_join_request_id ? (
                    <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
                      Workspace provider join request #{item.salon_join_request_id} is
                      attached to this rental path.
                    </div>
                  ) : null}

                  <div className="mt-5 flex flex-wrap gap-3">
                    {item.workspace_booking_id ? (
                      <Link
                        to={`/account/rentals/by-booking/${item.workspace_booking_id}`}
                        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
                      >
                        View accepted summary
                      </Link>
                    ) : null}
                    {!item.dispute_open && String(item.transfer_status || "").toLowerCase() !== "released" ? (
                      <Link
                        to={`/account/rentals/${item.booking_id}/dispute`}
                        className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                      >
                        Open dispute
                      </Link>
                    ) : null}
                    <Link
                      to="/search"
                      className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
                    >
                      Request another workspace
                    </Link>
                    {(item.owner_upgrade_required ||
                      item.acceptance_snapshot?.role_progression?.next_step_path) && (
                      <Link
                        to={
                          item.acceptance_snapshot?.role_progression?.next_step_path ||
                          "/account/profile"
                        }
                        className="rounded-full border border-sky-300 px-4 py-2 text-sm font-semibold text-sky-700 hover:border-sky-400"
                      >
                        Complete next step
                      </Link>
                    )}
                  </div>
                </article>
                  );
                })()
              ))}
            </div>
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
